import { mapGetters } from 'vuex'
import { payload as accountActionPayload } from '@/components/RouteComponents/Accounts/actionPayload'
import { payload as seriesActionPayload } from '@/components/RouteComponents/Series/actionPayload'
import { payload as locationActionPayload } from '@/components/RouteComponents/Locations/actionPayload'
import { payload as setActionPayload } from '@/components/RouteComponents/Sets/actionPayload'
import { payload as freefieldActionPayload } from '@/components/RouteComponents/Freefields/actionPayload'
import { payload as taxcodeActionPayload } from '@/components/RouteComponents/Taxcodes/actionPayload'
import { payload as insurancecodeActionPayload } from '@/components/RouteComponents/Insurances/actionPayload'
import AutocompleteLocalSearch from '@/utils/helpers/Search/AutocompleteLocalSearch'
import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'

export const poFieldsMixin = {
  computed: {
    ...mapGetters({
      series: 'getAllSeries',
      locations: 'getAllLocations',
      sets: 'getAllSets',
      taxcodes: 'getAllTaxcodes',
      freefields: 'getAllFreefields'
    }),
    /**
     * Returns attributes for the currently selected batch
     * @return {*}
     */
    batchAttributes () {
      let ret = { bank: null, company: null, production: null, currency: null }
      try {
        if (this.form.batch && this.form.batch.value) {
          const { value } = this.form.batch
          const { getBankByBatchId, getCompanyByBatchId, getProductionByBatchId, getCurrencyByBatchId, getBatchById, getSeriesById, getLocationById, getSetById, getFreefieldById, getTaxcodeById, getInsuranceById } = this.$store.getters
          if (getBankByBatchId(value) && getBankByBatchId(value).id) {
            ret.bank = getBankByBatchId(value)
          }
          if (getCompanyByBatchId(value) && getCompanyByBatchId(value).id) {
            ret.company = getCompanyByBatchId(value)
          }
          if (getProductionByBatchId(value) && getProductionByBatchId(value).id) {
            ret.production = getProductionByBatchId(value)
          }
          if (getCurrencyByBatchId(value) && getCurrencyByBatchId(value).id) {
            ret.currency = getCurrencyByBatchId(value)
          }

          const batch = getBatchById(this.form.batch.value)
          // Load optional default values in batch
          if (batch.default_series) {
            ret.series = getSeriesById(batch.default_series)
          }
          if (batch.hasOwnProperty('default_series') && (batch.default_series)) {
            ret.series = getSeriesById(batch.default_series)
          }
          if (batch.hasOwnProperty('default_location') && (batch.default_location)) {
            ret.location = getLocationById(batch.default_location)
          }
          if (batch.hasOwnProperty('default_set') && (batch.default_set)) {
            ret.set = getSetById(batch.default_set)
          }
          if (batch.hasOwnProperty('default_free_field_1') && (batch.default_free_field_1)) {
            ret.free_field_1 = getFreefieldById(batch.default_free_field_1)
          }
          if (batch.hasOwnProperty('default_free_field_2') && (batch.default_free_field_2)) {
            ret.free_field_2 = getFreefieldById(batch.default_free_field_2)
          }
          if (batch.hasOwnProperty('default_free_field_3') && (batch.default_free_field_3)) {
            ret.free_field_3 = getFreefieldById(batch.default_free_field_3)
          }
          if (batch.hasOwnProperty('default_free_field_4') && (batch.default_free_field_4)) {
            ret.free_field_4 = getFreefieldById(batch.default_free_field_4)
          }
          if (batch.hasOwnProperty('default_tax_code') && (batch.default_tax_code)) {
            ret.tax_code = getTaxcodeById(batch.default_tax_code)
          }
          if (batch.hasOwnProperty('default_insurance') && (batch.default_insurance)) {
            ret.insurance = getInsuranceById(batch.default_insurance)
          }
        }
        return ret
      } catch (e) {
        console.log(e)
        return ret
      }
    },
    /**
     * Returns all postable accounts, filtering out all but expense accounts since this is for POs
     * @return {*}
     */
    postableAccounts () {
      return this.$store.getters.getAllAccounts.filter(account => account.postable)
    },
    /**
     * Returns the code for the current company, based on the value of the current batch
     * @return {*}
     */
    currentCompany () {
      const { getCompanyByBatchId } = this.$store.getters
      if (this.form.batch && this.form.batch.value && getCompanyByBatchId(this.form.batch.value)) {
        return getCompanyByBatchId(this.form.batch.value).code
      }
      return ''
    },
    /**
     * Returns the code for the current production, based on the value of the current batch
     * @return {*}
     */
    currentProduction () {
      const { getProductionByBatchId } = this.$store.getters
      if (this.form.batch && this.form.batch.value && getProductionByBatchId(this.form.batch.value)) {
        return getProductionByBatchId(this.form.batch.value).code
      }
      return ''
    },
    /**
     * Returns distribution line field definitions for POs
     * @return {*}
     */
    lineFields () {
      let fields = [
        {
          name: 'line_number',
          label: 'Line',
          dataType: 'number',
          fieldType: 'input',
          disabled: true,
          fieldWidth: '2%'
        },
        {
          name: 'company',
          label: 'Co',
          dataType: 'string',
          fieldType: 'display',
          displayVal: this.currentCompany,
          fieldWidth: '4%',
          hidden: true
        },
        {
          name: 'production',
          label: 'Prod',
          dataType: 'string',
          fieldType: 'display',
          displayVal: this.currentProduction,
          fieldWidth: '4%',
          hidden: true
        },
        {
          name: 'account',
          label: 'Account',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.postableAccounts, accountActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '6%',
          readOnly: this.readOnly,
          actionPayload: accountActionPayload
        },
        {
          name: 'account:name',
          label: 'Account Name',
          dataType: 'string',
          fieldType: 'display',
          displayVal: (accountId) => {
            const acct = this.$store.getters.getAccountById(accountId)
            return acct && acct.id ? acct.name : ''
          },
          fieldWidth: '8%',
          hidden: false
        },
        {
          name: 'description',
          label: 'Description',
          dataType: 'string',
          fieldType: 'input',
          required: true,
          fieldWidth: '19%',
          readOnly: this.readOnly
        },
        {
          name: 'other_name',
          label: 'Other Name',
          dataType: 'string',
          fieldType: 'input',
          required: false,
          fieldWidth: '9%',
          hidden: true,
          readOnly: this.readOnly
        },
        {
          name: 'amount_decimal',
          label: 'Amount',
          dataType: 'string',
          fieldType: 'money',
          currency: this.currency && this.currency.symbol ? this.currency.symbol : '$',
          required: true,
          fieldWidth: '8%',
          readOnly: this.readOnly
        },
        {
          name: 'remaining_decimal',
          label: 'Remaining',
          dataType: 'string',
          fieldType: 'money',
          currency: this.currency && this.currency.symbol ? this.currency.symbol : '$',
          required: false,
          fieldWidth: '6%',
          readOnly: true
        },
        {
          name: 'close_reopen_id',
          label: '',
          dataType: 'number',
          fieldType: 'input',
          required: false,
          hidden: true
        },
        {
          name: 'location',
          label: 'Loc',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.locations, locationActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '4%',
          readOnly: false,
          actionPayload: locationActionPayload
        },
        {
          name: 'series',
          label: 'Series',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.series, seriesActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '5%',
          readOnly: false,
          actionPayload: seriesActionPayload
        },
        {
          name: 'set',
          label: 'Set',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.sets, setActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '5%',
          readOnly: false,
          actionPayload: setActionPayload
        },
        {
          name: 'tax_code',
          label: 'Tax code',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.taxcodes, taxcodeActionPayload).createOptions(),
          remote: false,
          fieldWidth: '5%',
          readOnly: this.readOnly,
          actionPayload: taxcodeActionPayload
        },
        {
          name: 'free_field_1',
          label: 'FF 1',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '4%',
          readOnly: this.readOnly,
          actionPayload: freefieldActionPayload
        },
        {
          name: 'free_field_2',
          label: 'FF 2',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '4%',
          readOnly: this.readOnly,
          actionPayload: freefieldActionPayload
        },
        {
          name: 'free_field_3',
          label: 'FF 3',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '4%',
          hidden: true,
          readOnly: this.readOnly,
          actionPayload: freefieldActionPayload
        },
        {
          name: 'free_field_4',
          label: 'FF 4',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '4%',
          hidden: true,
          readOnly: this.readOnly,
          actionPayload: freefieldActionPayload
        },
        {
          name: 'asset',
          label: 'Asset',
          dataType: 'boolean',
          fieldType: 'checkbox',
          fieldWidth: '2%',
          readOnly: this.readOnly
        },
        {
          name: 'third_party_vendor',
          label: '3rd',
          dataType: 'string',
          fieldType: 'autocomplete',
          searchAsync: true,
          searchOpts: { endpoint: 'vendors', query: {} },
          remote: true,
          fieldWidth: '5%',
          readOnly: this.readOnly
        },
        {
          name: 'source_type',
          label: '',
          dataType: 'string',
          fieldType: 'input',
          required: false,
          hidden: true
        }
      ]
      fields = this.setCustomFieldOrder(fields)
      if (typeof this.closeLine === 'function') { // If we're in PO edit view, add these fields
        let poEditViewFields = [
          {
            name: 'type',
            label: 'Type',
            dataType: 'string',
            fieldType: 'input',
            defaultVal: 'OPEN',
            fieldWidth: '3%',
            readOnly: true
          },
          {
            name: 'line_action',
            label: 'Action',
            fieldType: 'button',
            fieldWidth: '3%'
          }
        ]
        fields = [ ...fields, ...poEditViewFields ]
      }
      return fields
    }
  },
  methods: {
    /**
     * Get default data if they are not in store
     */
    async getDefaultData () {
      try {
        const {
          getAllSets,
          getAllLocations,
          getAllSeries,
          getAllFreefields,
          getAllTaxcodes,
          getAllInsurances
        } = this.$store.getters

        let defaultDataPayloads = []
        if (!getAllSets.length) {
          defaultDataPayloads.push(setActionPayload)
        }
        if (!getAllLocations.length) {
          defaultDataPayloads.push(locationActionPayload)
        }
        if (!getAllSeries.length) {
          defaultDataPayloads.push(seriesActionPayload)
        }
        if (!getAllFreefields.length) {
          defaultDataPayloads.push(freefieldActionPayload)
        }
        if (!getAllTaxcodes.length) {
          defaultDataPayloads.push(taxcodeActionPayload)
        }
        if (!getAllInsurances.length) {
          defaultDataPayloads.push(insurancecodeActionPayload)
        }
        let dataLoader = new DataLoader(this.$store, defaultDataPayloads, true, 200)
        dataLoader.loadData()
      } catch (e) {
        console.log(e)
      }
    }
  },
  created () {
    this.getDefaultData()
  }
}



// WEBPACK FOOTER //
// ./src/Mixins/PoFields.js